const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.style.setProperty('--animation-order', index);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -10px 0px',
  });
  
  const viewportHeight = window.innerHeight;
  
  // READ phase: Measure everything first without causing layout reflows in between
  const elementStates = animationTriggerElements.map((element) => {
    return {
      element,
      inViewport: element.getBoundingClientRect().top < viewportHeight + 50
    };
  });

  // WRITE phase: Update the DOM classes in batch
  elementStates.forEach(({ element, inViewport }) => {
    // ATB bypass: don't animate elements already in viewport
    if (inViewport) {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    } else {
      observer.observe(element);
    }
  });
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2; // 20% max scale
  const DEADBAND = 0.005; // Skip updates smaller than this threshold

  // Optimization: Only animate elements that are currently intersecting the viewport
  const activeElements = new Set();
  let rafPending = false; // Prevent rAF stacking
  
  const handleScroll = () => {
    if (activeElements.size === 0) return;
    if (rafPending) return; // Skip if previous frame hasn't completed
    rafPending = true;
    
    window.requestAnimationFrame(() => {
      rafPending = false;
      const viewHeight = window.innerHeight;
      const scrollY = window.scrollY;
      
      activeElements.forEach((element) => {
        // Optimization: Use cached position data instead of getBoundingClientRect to avoid layout thrashing
        if (!element.cachedPos) {
           const rect = element.getBoundingClientRect();
           element.cachedPos = {
             top: rect.top + scrollY,
             height: rect.height
           };
        }
        
        const rectTop = element.cachedPos.top - scrollY;
        const rectHeight = element.cachedPos.height;

        if (rectTop <= viewHeight && rectTop + rectHeight >= 0) {
          let percentageSeen = (viewHeight - rectTop) / (viewHeight + rectHeight);
          percentageSeen = Math.max(0, Math.min(1, percentageSeen));
          const newRatio = 1 + (scaleAmount * percentageSeen);
          
          // Deadband: only update if change exceeds threshold
          const currentRatio = element._lastZoomRatio || 1;
          if (Math.abs(newRatio - currentRatio) > DEADBAND) {
            element._lastZoomRatio = newRatio;
            element.style.setProperty('--zoom-in-ratio', newRatio);
          }
        }
      });
    });
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Clear cache on intersection to ensure fresh values if layout shifted
        entry.target.cachedPos = null; 
        activeElements.add(entry.target);
      } else {
        activeElements.delete(entry.target);
        // Reset zoom when leaving viewport to free GPU layer
        entry.target._lastZoomRatio = 1;
        entry.target.style.setProperty('--zoom-in-ratio', 1);
      }
    });

    // Start/Stop scroll listener based on active elements
    if (activeElements.size > 0) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    } else {
      window.removeEventListener('scroll', handleScroll);
      rafPending = false;
    }
  }, {
    rootMargin: '100px 0px 100px 0px' // Start tracking slightly before they enter view
  });

  animationTriggerElements.forEach(element => observer.observe(element));
  
  // Initial run
  setTimeout(handleScroll, 100); 
}


window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
